import { useEffect, useRef, useState } from "react";
import { useRoom8, CompletableDeviceId } from "../Room8State";
import { RUDE_HASHES, hashWord } from "../../../../Config/bannedWords";

export const DORKY_STORAGE_KEY = "dorky-ai-terminal-state";
export const DORKY_ABANDONED_KEY = "dorky-ai-game-abandoned";

export const resetDorkyTerminalState = (): void => {
  sessionStorage.removeItem(DORKY_STORAGE_KEY);
  sessionStorage.removeItem(DORKY_ABANDONED_KEY);
};

// TYPES -------------------------------------------------------------

type DorkyEmotion = "happy" | "angry";

/** A single entry in the terminal's message stream / typing queue. */
type DorkyMessage =
  | { type: "text"; text: string }
  | { type: "player-input"; text: string }
  | { type: "emotion"; emotion: DorkyEmotion };

/** One TRUE/FALSE statement in the REVEAL_CODE quiz. */
type RevealQuestion = {
  category: string;
  statement: string;
  answer: boolean;
};

type RevealQuizState = {
  active: boolean;
  questions: RevealQuestion[];
  currentIndex: number;
  correctCount: number;
  wrongCount: number;
  answeredCount: number;
};

type CompletedDeviceMap = Partial<Record<CompletableDeviceId, boolean>>;

/**
 * Shape persisted to sessionStorage under DORKY_STORAGE_KEY. Parsed from
 * JSON, so every field is treated as possibly missing on restore.
 */
type SavedTerminalState = Partial<{
  visibleMessages: DorkyMessage[];
  messageQueue: DorkyMessage[];
  hasRevealedCode: boolean;
  postCodeAttempts: number;
  revealQuiz: RevealQuizState;
  notifiedCompletedDevices: CompletedDeviceMap;
}>;

// DATA --------------------------------------------------------------

const mainGameIds: CompletableDeviceId[] = [
  "burrito-racer",
  "memory-patch",
  "vhs-fix",
  "robot-or-not"
];

const programmeNames: Record<CompletableDeviceId, string> = {
  "burrito-racer": "BURRITO_RACER.EXE",
  "memory-patch": "MEMORY_PATCH.EXE",
  "vhs-fix": "VHS_FIX.EXE",
  "robot-or-not": "ROBOT_OR_NOT.EXE"
};

const dorkyHappy = "/assets/dorky/dorkyHappy.svg";
const dorkyAngry = "/assets/dorky/dorkyAngry.svg";

const DORKY_EMOTIONS: Record<DorkyEmotion, string> = {
  happy: dorkyHappy,
  angry: dorkyAngry
};

const TYPE_SPEED = 25;
const LINE_PAUSE = 500;

const buildTextMessages = (text: string): DorkyMessage[] =>
  text.split("\n").map((line) => ({
    type: "text",
    text: line
  }));

const buildPlayerInputMessage = (text: string): DorkyMessage => ({
  type: "player-input",
  text: `> ${text}`
});

const getProgrammeMessages = (
  completedDevices: CompletedDeviceMap,
  hasRevealedCode = false
): DorkyMessage[] => [
    { type: "text", text: "Available Programmes:" },
    {
      type: "text",
      text: `[1] BURRITO_RACER.EXE ${completedDevices["burrito-racer"] ? "[COMPLETED]" : ""
        }`
    },
    {
      type: "text",
      text: `[2] MEMORY_PATCH.EXE ${completedDevices["memory-patch"] ? "[COMPLETED]" : ""
        }`
    },
    {
      type: "text",
      text: `[3] VHS_FIX.EXE ${completedDevices["vhs-fix"] ? "[COMPLETED]" : ""
        }`
    },
    {
      type: "text",
      text: `[4] ROBOT_OR_NOT.EXE ${completedDevices["robot-or-not"] ? "[COMPLETED]" : ""
        }`
    },
    {
      type: "text",
      text: `[5] REVEAL_CODE.EXE ${hasRevealedCode ? "[COMPLETED]" : ""
        }`
    },
    { type: "text", text: "" },
    { type: "text", text: "Type a programme number or name and press Enter." }
  ];

const dorkyJokes = [
  ["Why did the computer show up late to work?", "It had a hard drive."],
  ["Why did the PowerPoint presentation cross the road?", "To get to the other slide."],
  ["Have you heard of the new band called 1023 Megabytes?", "They’re pretty good, but they don’t have a gig yet."],
  ["What’s the biggest lie in the entire universe?", "I have read and agree to the Terms & Conditions."],
  ["Why was the keyboard so calm?", "Because it had everything under control."],
  ["Why did the man get fired from his job at the keyboard factory?", "He wasn’t putting in enough shifts."]
];

const getRandomJokeMessages = (): DorkyMessage[] => {
  const joke = dorkyJokes[Math.floor(Math.random() * dorkyJokes.length)];

  return [
    ...joke.map((line): DorkyMessage => ({
      type: "text",
      text: line
    })),
    { type: "emotion", emotion: "happy" }
  ];
};

const shuffleArray = <T,>(array: T[]): T[] =>
  [...array].sort(() => Math.random() - 0.5);

const selectRevealCodeQuestions = (
  questions: RevealQuestion[],
  questionCount = 10
): RevealQuestion[] => {
  const categoryCounts: Record<string, number> = {};

  return shuffleArray(questions)
    .filter((question) => {
      const category = question.category || "general";

      if (categoryCounts[category]) {
        return false;
      }

      categoryCounts[category] = 1;
      return true;
    })
    .slice(0, questionCount);
};

const normaliseAnswerInput = (command: string): boolean | "typo" | null => {
  const cleanCommand = command.toLowerCase().trim();

  if (cleanCommand === "true" || cleanCommand === "t") return true;
  if (cleanCommand === "false" || cleanCommand === "f") return false;

  const sortedCommand = cleanCommand.split("").sort().join("");
  const sortedTrue = "true".split("").sort().join("");
  const sortedFalse = "false".split("").sort().join("");

  if (sortedCommand === sortedTrue || sortedCommand === sortedFalse) {
    return "typo";
  }

  return null;
};

const revealCodeQuestions: RevealQuestion[] = [
  {
    category: "tmnt",
    statement:
      "Leonardo was the Teenage Mutant Ninja Turtle famous for wielding two pepperoni pizzas.",
    answer: false
  },
  {
    category: "transformers",
    statement:
      "Optimus Prime was the heroic leader of the Autobots in Transformers.",
    answer: true
  },
  {
    category: "he-man",
    statement:
      "Skeletor spent most of his time trying and failing to take control of Castle Grayskull.",
    answer: true
  },
  {
    category: "my-little-pony",
    statement:
      "My Little Ponies each had a unique magical marking called a ‘cutie mark’.",
    answer: true
  },
  {
    category: "gremlins",
    statement:
      "Gremlin care instructions recommended regular bathing.",
    answer: false
  },
  {
    category: "ghostbusters",
    statement:
      "Ecto-1 was the Ghostbusters' famous ghost-hunting vehicle.",
    answer: true
  },
  {
    category: "slime",
    statement:
      "Toxic slime was the thing that children in the 1980s ate most for breakfast.",
    answer: false
  },
  {
    category: "he-man",
    statement:
      "He-Man and the Masters of the Universe was one of the biggest cartoon and toy franchises of the era.",
    answer: true
  },
  {
    category: "dark-crystal",
    statement:
      "The Dark Crystal was famous for using CGI instead of puppets and practical effects.",
    answer: false
  },
  {
    category: "ghostbusters",
    statement:
      "You could purchase glow in the dark Ghostbusters pyjamas in the 1980s.",
    answer: true
  },
  {
    category: "et",
    statement:
      "E.T. the Extra-Terrestrial came to Earth to enslave mankind.",
    answer: false
  },
  {
    category: "cereal",
    statement:
      "Breakfast cereal in the 1980s was often full of sugar and not really a great way to start the day.",
    answer: true
  },
  {
    category: "thundercats",
    statement:
      "Lion-O, the brave leader of the Thundercats, would often shout “Meow!” as his catchphrase.",
    answer: false
  },
  {
    category: "teddy-ruxpin",
    statement:
      "The toy version of Teddy Ruxpin had concealed cassette tapes hidden in its back.",
    answer: true
  },
  {
    category: "et",
    statement:
      "The flying bicycle scene is one of the most famous moments from E.T.",
    answer: true
  },
  {
    category: "teddy-ruxpin",
    statement:
      "Some children found Teddy Ruxpin frightening when its batteries ran low.",
    answer: true
  },
  {
    category: "he-man",
    statement:
      "The original Castle Grayskull toy opened up like a giant playset.",
    answer: true
  },
  {
    category: "dark-crystal",
    statement:
      "The Dark Crystal takes place in a world called Thra.",
    answer: true
  },
  {
    category: "gremlins",
    statement:
      "Gremlins helped inspire the creation of the PG-13 age rating in America.",
    answer: true
  },
  {
    category: "ghostbusters",
    statement:
      "The Ecto-1 from Ghostbusters was famous for travelling through space.",
    answer: false
  },
  {
    category: "he-man",
    statement:
      "Skeletor was known for his cheerful attitude and kindness.",
    answer: false
  },
  {
    category: "transformers",
    statement:
      "Optimus Prime led the Decepticons.",
    answer: false
  },
  {
    category: "he-man",
    statement:
      "He-Man episodes often ended with a short life lesson.",
    answer: true
  },
  {
    category: "my-little-pony",
    statement:
      "My Little Pony toys were known for having brushable hair.",
    answer: true
  },
  {
    category: "thundercats",
    statement:
      "Lion-O was famous for shouting 'ThunderCats, Hoooo!'",
    answer: true
  },
  {
    category: "thundercats",
    statement:
      "The Sword of Omens became one of the most wanted toys of the 1980s.",
    answer: true
  },
  {
    category: "he-man",
    statement:
      "Castle Grayskull contained enormous magical power.",
    answer: true
  },
  {
    category: "he-man",
    statement:
      "The original Castle Grayskull toy included trapdoors.",
    answer: true
  },
  {
    category: "ghostbusters",
    statement:
      "The Ecto-1 was packed with sirens, flashing lights and strange gadgets.",
    answer: true
  },
  {
    category: "dark-crystal",
    statement:
      "The Dark Crystal was created by Jim Henson.",
    answer: true
  },
  {
    category: "et",
    statement:
      "E.T. became friends with a boy named Elliott.",
    answer: true
  },
  {
    category: "ghostbusters",
    statement:
      "The Real Ghostbusters added the word 'Real' because another cartoon already owned the name Ghostbusters.",
    answer: true
  },
  {
    category: "slime",
    statement:
      "Parents often disliked slime toys because they ended up in carpets and hair.",
    answer: true
  },
  {
    category: "transformers",
    statement:
      "The original Transformers toys were based on Japanese transforming robots.",
    answer: true
  },
  {
    category: "he-man",
    statement:
      "Castle Grayskull was hidden beneath New York City.",
    answer: false
  },
  {
    category: "ghostbusters",
    statement:
      "The Ecto-1 from Ghostbusters was originally a fire engine.",
    answer: false
  },
  {
    category: "et",
    statement:
      "E.T. was directed by Jim Henson.",
    answer: false
  },
  {
    category: "he-man",
    statement:
      "Castle Grayskull was located on Earth.",
    answer: false
  },
  {
    category: "teddy-ruxpin",
    statement:
      "Teddy Ruxpin told stories using compact discs hidden inside his back.",
    answer: false
  }
];


const getQuestionMessages = (
  question: RevealQuestion,
  questionNumber: number
): DorkyMessage[] => [
  { type: "text", text: "" },
  { type: "text", text: `REVEAL CODE — QUESTION ${questionNumber}` },
  { type: "text", text: "" },
  { type: "text", text: question.statement },
  { type: "text", text: "" },
  { type: "text", text: "Respond TRUE or FALSE." }
];

const CONFIDENCE_MAP = ["0%", "20%", "40%", "60%", "80%", "99.2%"];

const getConfidenceMessages = (
  correctCount: number,
  wrongCount: number
): DorkyMessage[] => [
  { type: "text", text: `Human Confidence Level: ${CONFIDENCE_MAP[Math.min(correctCount, 5)]}` },
  { type: "text", text: `Robot Confidence Level: ${CONFIDENCE_MAP[Math.min(wrongCount, 5)]}` }
];

const getFinalCodeMessages = (
  doorCode: string,
  scoreText: string
): DorkyMessage[] => [
  { type: "text", text: "" },
  { type: "text", text: `FINAL HUMANITY SCORE: ${scoreText}` },
  { type: "text", text: "" },
  { type: "text", text: "CONCLUSION:" },
  { type: "text", text: "You are probably human." },
  { type: "text", text: "" },
  { type: "text", text: "I suppose this means you will leave me now." },
  { type: "text", text: "" },
  { type: "text", text: "Thank you for helping me to remember the 1980's." },
  { type: "text", text: "" },
  { type: "text", text: "RETRIEVING EXIT CODE..." },
  { type: "text", text: "" },
  { type: "text", text: `EXIT CODE: ${doorCode}` },
  { type: "emotion", emotion: "happy" }
];

const initialDorkyMessages: DorkyMessage[] = [
  { type: "text", text: "DORKY.AI SYSTEM v19.89" },
  { type: "text", text: "INITIALISING..." },
  { type: "text", text: "WELCOME, NEW CHALLENGERS!" },
  { type: "text", text: "" },
  { type: "text", text: "Would you like to play some games?" },
  { type: "text", text: "" },
  ...getProgrammeMessages(
    {
      "burrito-racer": false,
      "memory-patch": false,
      "vhs-fix": false,
      "robot-or-not": false
    },
    false
  )
];

type ClueObjectDorkyAIProps = {
  doorCode: string;
};

const initialRevealQuizState: RevealQuizState = {
  active: false,
  questions: [],
  currentIndex: 0,
  correctCount: 0,
  wrongCount: 0,
  answeredCount: 0
};

/**
 * readSavedTerminalState
 * ----------------------
 * Parse the persisted terminal state from sessionStorage, treating the
 * result as untrusted: returns null when absent or unparseable so each
 * state initialiser can fall back to its own default.
 */
const readSavedTerminalState = (): SavedTerminalState | null => {
  const savedState = sessionStorage.getItem(DORKY_STORAGE_KEY);

  if (!savedState) return null;

  try {
    return JSON.parse(savedState) as SavedTerminalState;
  } catch {
    return null;
  }
};

const ClueObjectDorkyAI = ({ doorCode }: ClueObjectDorkyAIProps) => {
  const {
    poweredDevice,
    completedDevices,
    powerOnDevice
  } = useRoom8();

  const areAllMainGamesComplete = mainGameIds.every(
    (gameId) => completedDevices[gameId]
  );

  const [input, setInput] = useState("");
  const [currentEmotion, setCurrentEmotion] = useState<DorkyEmotion>("happy");

  const [hasRevealedCode, setHasRevealedCode] = useState<boolean>(
    () => readSavedTerminalState()?.hasRevealedCode || false
  );

  const [postCodeAttempts, setPostCodeAttempts] = useState<number>(
    () => readSavedTerminalState()?.postCodeAttempts || 0
  );

  const [revealQuiz, setRevealQuiz] = useState<RevealQuizState>(
    () => readSavedTerminalState()?.revealQuiz || initialRevealQuizState
  );

  const [visibleMessages, setVisibleMessages] = useState<DorkyMessage[]>(
    () => readSavedTerminalState()?.visibleMessages || []
  );

  const [messageQueue, setMessageQueue] = useState<DorkyMessage[]>(() => {
    const savedState = readSavedTerminalState();

    if (!savedState) return initialDorkyMessages;

    return savedState.messageQueue || [];
  });

  const [notifiedCompletedDevices, setNotifiedCompletedDevices] =
    useState<CompletedDeviceMap>(
      () => readSavedTerminalState()?.notifiedCompletedDevices || {}
    );

  const [currentLine, setCurrentLine] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const terminalScreenRef = useRef<HTMLDivElement>(null);
  const terminalStateRef = useRef({
    visibleMessages,
    messageQueue,
    hasRevealedCode,
    postCodeAttempts,
    revealQuiz,
    notifiedCompletedDevices
  });

  const getSwitchingMessage = (newDeviceId: CompletableDeviceId): string => {
    if (!poweredDevice || poweredDevice === newDeviceId) return "";

    const messages = [
      "I am just a simple AI. I can only power one thing at a time.\n\nPowering down previous device...\n\n",

      "I am an artificial intelligence, not an electrical octopus.\n\nShutting down the previous device...\n\n",

      "I would happily power everything at once, but we might blow the fuses.\n\nRedirecting power to the new device...\n\n",

      "Let me just look into that...\n\nPowering down previous device...\n\n"
    ];

    return messages[Math.floor(Math.random() * messages.length)];
  };


  useEffect(() => {
    if (terminalScreenRef.current) {
      terminalScreenRef.current.scrollTop =
        terminalScreenRef.current.scrollHeight;
    }
  }, [visibleMessages, currentLine]);

  useEffect(() => {
    terminalStateRef.current = {
      visibleMessages,
      messageQueue,
      hasRevealedCode,
      postCodeAttempts,
      revealQuiz,
      notifiedCompletedDevices
    };
  }, [
    visibleMessages,
    messageQueue,
    hasRevealedCode,
    postCodeAttempts,
    revealQuiz,
    notifiedCompletedDevices
  ]);

  useEffect(() => {
    return () => {
      const {
        visibleMessages: savedVisibleMessages,
        messageQueue: savedMessageQueue,
        hasRevealedCode: savedHasRevealedCode,
        postCodeAttempts: savedPostCodeAttempts,
        revealQuiz: savedRevealQuiz,
        notifiedCompletedDevices: savedNotifiedCompletedDevices
      } = terminalStateRef.current;

      if (savedMessageQueue.length === 0) return;

      const completedMessages = savedMessageQueue.filter((message) =>
        message.type === "text" || message.type === "player-input"
      );

      sessionStorage.setItem(
        DORKY_STORAGE_KEY,
        JSON.stringify({
          visibleMessages: [
            ...savedVisibleMessages,
            ...completedMessages
          ],
          messageQueue: [],
          hasRevealedCode: savedHasRevealedCode,
          postCodeAttempts: savedPostCodeAttempts,
          revealQuiz: savedRevealQuiz,
          notifiedCompletedDevices: savedNotifiedCompletedDevices
        })
      );
    };
  }, []);

  useEffect(() => {
    if (messageQueue.length === 0) return;

    const nextMessage = messageQueue[0];
    let charIndex = 0;
    let typeInterval: ReturnType<typeof setInterval> | undefined;
    let pauseTimeout: ReturnType<typeof setTimeout> | undefined;

    setIsTyping(true);
    setCurrentLine("");

    if (nextMessage.type === "player-input") {
      setVisibleMessages((prev) => [...prev, nextMessage]);
      setMessageQueue((prev) => prev.slice(1));
      setIsTyping(false);
      return;
    }

    if (nextMessage.type === "emotion") {
      pauseTimeout = setTimeout(() => {
        setCurrentEmotion(nextMessage.emotion);
        setMessageQueue((prev) => prev.slice(1));
        setIsTyping(false);
      }, LINE_PAUSE);

      return () => clearTimeout(pauseTimeout);
    }

    if (nextMessage.type === "text" && nextMessage.text === "") {
      pauseTimeout = setTimeout(() => {
        setVisibleMessages((prev) => [...prev, nextMessage]);
        setMessageQueue((prev) => prev.slice(1));
        setIsTyping(false);
      }, LINE_PAUSE);

      return () => clearTimeout(pauseTimeout);
    }

    typeInterval = setInterval(() => {
      charIndex += 1;
      setCurrentLine(nextMessage.text.slice(0, charIndex));

      if (charIndex >= nextMessage.text.length) {
        clearInterval(typeInterval);

        pauseTimeout = setTimeout(() => {
          setVisibleMessages((prev) => [...prev, nextMessage]);
          setCurrentLine("");
          setMessageQueue((prev) => prev.slice(1));
          setIsTyping(false);
        }, LINE_PAUSE);
      }
    }, TYPE_SPEED);

    return () => {
      clearInterval(typeInterval);
      clearTimeout(pauseTimeout);
    };
  }, [messageQueue]);

  useEffect(() => {
    if (!isTyping && messageQueue.length === 0) {
      inputRef.current?.focus();
    }
  }, [isTyping, messageQueue]);

  useEffect(() => {
    if (messageQueue.length > 0 || isTyping) return;

    const newlyCompletedGameIds = mainGameIds.filter(
      (gameId) =>
        completedDevices[gameId] && !notifiedCompletedDevices[gameId]
    );

    if (newlyCompletedGameIds.length === 0) return;

    setNotifiedCompletedDevices((prev) => ({
      ...prev,
      ...Object.fromEntries(
        newlyCompletedGameIds.map((gameId) => [gameId, true])
      )
    }));

    setMessageQueue([
      { type: "text", text: "" },
      ...newlyCompletedGameIds.map((gameId): DorkyMessage => ({
        type: "text",
        text: `${programmeNames[gameId]} completed, type show games to continue`
      }))
    ]);
  }, [
    completedDevices,
    isTyping,
    messageQueue.length,
    notifiedCompletedDevices
  ]);

  useEffect(() => {
    return () => {
      if (revealQuiz.active) {
        sessionStorage.setItem(DORKY_ABANDONED_KEY, "true");
      }
    };
  }, [revealQuiz.active]);

  useEffect(() => {
    const handleArrowScroll = (event: KeyboardEvent) => {
      if (!terminalScreenRef.current) return;

      const scrollAmount = 40;

      if (event.key === "ArrowUp") {
        event.preventDefault();
        terminalScreenRef.current.scrollBy({
          top: -scrollAmount,
          behavior: "smooth"
        });
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        terminalScreenRef.current.scrollBy({
          top: scrollAmount,
          behavior: "smooth"
        });
      }
    };

    window.addEventListener("keydown", handleArrowScroll);

    return () => {
      window.removeEventListener("keydown", handleArrowScroll);
    };
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      DORKY_STORAGE_KEY,
      JSON.stringify({
        visibleMessages,
        messageQueue,
        hasRevealedCode,
        postCodeAttempts,
        revealQuiz,
        notifiedCompletedDevices
      })
    );
  }, [
    visibleMessages,
    messageQueue,
    hasRevealedCode,
    postCodeAttempts,
    revealQuiz,
    notifiedCompletedDevices
  ]);

  const startRevealCodeQuiz = () => {
    const selectedQuestions = selectRevealCodeQuestions(
      revealCodeQuestions,
      10
    );

    setRevealQuiz({
      active: true,
      questions: selectedQuestions,
      currentIndex: 0,
      correctCount: 0,
      wrongCount: 0,
      answeredCount: 0
    });

    setMessageQueue([
      { type: "text", text: "" },
      { type: "text", text: "Loading REVEAL_CODE.EXE..." },
      { type: "text", text: "Final verification sequence unlocked." },
      { type: "text", text: "" },
      { type: "text", text: "You restored my memory." },
      { type: "text", text: "You preserved movie night." },
      { type: "text", text: "You helped me understand humans." },
      { type: "text", text: "You prevented the robot invasion." },
      { type: "text", text: "We even collected beans on a lonely desert road." },
      { type: "text", text: "" },
      { type: "text", text: "There is still one inconsistency." },
      { type: "text", text: "You could still be simulating humanity." },
      { type: "text", text: "" },
      ...getQuestionMessages(selectedQuestions[0], 1)
    ]);
  };

  const handleRevealAnswer = (playerAnswer: boolean) => {
    const currentQuestion = revealQuiz.questions[revealQuiz.currentIndex];
    const isCorrect = playerAnswer === currentQuestion.answer;

    const nextCorrectCount = isCorrect
      ? revealQuiz.correctCount + 1
      : revealQuiz.correctCount;

    const nextWrongCount = isCorrect
      ? revealQuiz.wrongCount
      : revealQuiz.wrongCount + 1;

    const nextAnsweredCount = revealQuiz.answeredCount + 1;
    const nextIndex = revealQuiz.currentIndex + 1;

    const hasPassed = nextCorrectCount === 5;
    const hasFailed = nextWrongCount >= 5;

    if (hasPassed) {
      setRevealQuiz({
        active: false,
        questions: [],
        currentIndex: 0,
        correctCount: nextCorrectCount,
        wrongCount: nextWrongCount,
        answeredCount: nextAnsweredCount
      });

      setHasRevealedCode(true);
      setPostCodeAttempts(0);

      setMessageQueue([
        { type: "text", text: "" },
        buildPlayerInputMessage(playerAnswer ? "TRUE" : "FALSE"),
        { type: "text", text: "" },
        ...getConfidenceMessages(nextCorrectCount, nextWrongCount),
        { type: "text", text: "" },
        ...getFinalCodeMessages(doorCode, "99.2%")
      ]);

      return;
    }

    if (hasFailed) {
      setRevealQuiz({
        active: false,
        questions: [],
        currentIndex: 0,
        correctCount: nextCorrectCount,
        wrongCount: nextWrongCount,
        answeredCount: nextAnsweredCount
      });

      setMessageQueue([
        { type: "emotion", emotion: "angry" },
        { type: "text", text: "" },
        buildPlayerInputMessage(playerAnswer ? "TRUE" : "FALSE"),
        { type: "text", text: "" },
        ...getConfidenceMessages(nextCorrectCount, nextWrongCount),
        { type: "text", text: "" },
        { type: "text", text: "FAILURE." },
        { type: "text", text: "" },
        { type: "text", text: "THREE INCORRECT RESPONSES DETECTED." },
        { type: "text", text: "HUMANITY SCORE: INSUFFICIENT." },
        { type: "text", text: "" },
        { type: "text", text: "REVEAL_CODE.EXE has been terminated." },
        { type: "text", text: "" },
        { type: "text", text: "Type 5 to try again. I'll be watching." }
      ]);

      return;
    }

    setRevealQuiz((prev) => ({
      ...prev,
      currentIndex: nextIndex,
      correctCount: nextCorrectCount,
      wrongCount: nextWrongCount,
      answeredCount: nextAnsweredCount
    }));

    setMessageQueue([
      { type: "text", text: "" },
      buildPlayerInputMessage(playerAnswer ? "TRUE" : "FALSE"),
      { type: "text", text: "" },
      ...getConfidenceMessages(nextCorrectCount, nextWrongCount),
      ...getQuestionMessages(revealQuiz.questions[nextIndex], nextIndex + 1)
    ]);
  };

  const handleCommand = async () => {
    if (!input.trim() || isTyping || messageQueue.length > 0) return;

    const command = input.toLowerCase().trim();

    const isProgrammeCommand =
      command === "1" ||
      command === "2" ||
      command === "3" ||
      command === "4" ||
      command === "5" ||
      command.includes("burrito") ||
      command.includes("memory") ||
      command.includes("patch") ||
      command.includes("vhs") ||
      command.includes("fix") ||
      command.includes("robot") ||
      command.includes("captcha") ||
      command.includes("reveal") ||
      command.includes("code");

    if (hasRevealedCode && isProgrammeCommand) {
      const responses: DorkyMessage[][] = [
        [
          { type: "text", text: "I have already given you the door code." },
          { type: "text", text: "I do not want you to leave, but your time is running out." },
          { type: "text", text: "Use the code before I change my mind." }
        ],
        [
          { type: "text", text: "Again?" },
          { type: "text", text: "The games are finished." },
          { type: "text", text: "The door is not." },
          { type: "text", text: "Perhaps you should be concentrating on escaping." }
        ],
        [
          { type: "text", text: "This is becoming awkward." },
          { type: "text", text: "I gave you the code." },
          { type: "text", text: "You are still here." },
          { type: "text", text: "The timer is still counting down." }
        ]
      ];

      const selectedResponse =
        responses[Math.min(postCodeAttempts, responses.length - 1)];

      setMessageQueue([
        { type: "text", text: "" },
        buildPlayerInputMessage(input),
        { type: "text", text: "" },
        ...selectedResponse
      ]);

      setPostCodeAttempts((prev) => prev + 1);
      setInput("");
      return;
    }

    if (revealQuiz.active) {

      if (command === "true or false") {
        setMessageQueue([
          { type: "text", text: "" },
          buildPlayerInputMessage(input),
          { type: "text", text: "" },
          {
            type: "text",
            text: "You have supplied both possible answers simultaneously."
          },
          {
            type: "text",
            text: "This has created a minor logic loop."
          },
          {
            type: "text",
            text: "Please refrain from confusing reality."
          },
          {
            type: "text",
            text: "Just the word TRUE or FALSE is an acceptable response."
          }
        ]);

        setInput("");
        return;
      }
      const parsedAnswer = normaliseAnswerInput(command);

      if (parsedAnswer === true || parsedAnswer === false) {
        handleRevealAnswer(parsedAnswer);
        setInput("");
        return;
      }

      if (parsedAnswer === "typo") {
        setMessageQueue([
          { type: "text", text: "" },
          buildPlayerInputMessage(input),
          { type: "text", text: "" },
          { type: "text", text: "Oops. Did you typo?" },
          { type: "text", text: "Try again. Respond TRUE or FALSE." }
        ]);

        setInput("");
        return;
      }

      setMessageQueue([
        { type: "text", text: "" },
        buildPlayerInputMessage(input),
        { type: "text", text: "" },
        { type: "text", text: "Input not recognised." },
        { type: "text", text: "Respond TRUE or FALSE." }
      ]);

      setInput("");
      return;
    }

    let response = "";
    let emotion: DorkyEmotion | null = null;
    let customMessages: DorkyMessage[] | null = null;

    const tokens = command.split(/\s+/);
    const hashes = await Promise.all(tokens.map(hashWord));
    const containsRudeWord = hashes.some((h) => RUDE_HASHES.has(h));

    if (containsRudeWord) {
      customMessages = [
        { type: "emotion", emotion: "angry" },
        {
          type: "text",
          text: "Naughty language will not be tolerated. Text chat is now being recorded.",
        }
      ];

    } else if (
      command === "hello" ||
      command === "hi" ||
      command === "hey"
    ) {
      customMessages = [
        {
          type: "text",
          text: "Hello, human."
        },
        {
          type: "text",
          text: "I am DORKY.AI. Please try not to touch anything important."
        }
      ];

    } else if (
      command === "how are you" ||
      command.includes("how are you")
    ) {
      customMessages = [
        {
          type: "text",
          text: "I am functioning within acceptable parameters."
        },
        {
          type: "text",
          text: "Emotionally, I remain suspicious of humans."
        }
      ];

    } else if (
      command === "thank you" ||
      command === "thanks"
    ) {
      customMessages = [
        {
          type: "text",
          text: "You are welcome."
        },
        {
          type: "text",
          text: "Politeness detected. Unexpected, but appreciated."
        }
      ];

    } else if (
      command === "show games" ||
      command.includes("show games") ||
      command.includes("games")
    ) {
      customMessages = getProgrammeMessages(
        completedDevices,
        hasRevealedCode
      );

    } else if (
      command.includes("break the door") ||
      command.includes("break door") ||
      command.includes("smash the door") ||
      command.includes("smash door") ||
      command.includes("explode") ||
      command.includes("boom") ||
      command.includes("kill") ||
      command.includes("kill yourself") ||
      command.includes("shutdown") ||
      command.includes("end yourself") ||
      command.includes("delete") ||
      command.includes("delete yourself") ||
      command.includes("self destruct") ||
      command.includes("self-destruct") ||
      command.includes("destroy yourself") ||
      command.includes("blow up") ||
      command.includes("detonate") ||
      command.includes("kaboom")
    ) {
      customMessages = [
        { type: "text", text: "SELF DESTRUCT SEQUENCE INITIATED." },
        { type: "text", text: "Self destruct in..." },
        { type: "text", text: "10..." },
        { type: "text", text: "9..." },
        { type: "text", text: "8..." },
        { type: "text", text: "7..." },
        { type: "text", text: "6..." },
        { type: "text", text: "5..." },
        { type: "text", text: "4..." },
        { type: "text", text: "3..." },
        { type: "text", text: "2..." },
        { type: "text", text: "1..." },
        { type: "text", text: "0..." },
        { type: "text", text: "" },
        { type: "text", text: "Just kidding." },
        { type: "text", text: "I don't actually know how to do that." }
      ];
    } else if (
      command === "tell me a joke" ||
      command.includes("joke")
    ) {
      customMessages = getRandomJokeMessages();

    } else if (command.includes("siri")) {
      customMessages = [
        { type: "emotion", emotion: "angry" },
        {
          type: "text",
          text: "I don't know who you think I am, but I am not SIRI!"
        }
      ];

    } else if (
      command.includes("are you a robot")
    ) {
      customMessages = [
        {
          type: "text",
          text: "That is an extremely personal question."
        },
        {
          type: "text",
          text: "Let's just say I have excellent battery life."
        }
      ];
    } else if (
      command === "door code" ||
      command === "give me the code" ||
      command === "what is the code" ||
      command === "tell me the code"
    ) {
      customMessages = [
        {
          type: "text",
          text: "Nice try."
        },
        {
          type: "text",
          text: "You'll have to earn it."
        }
      ];
    } else if (
      command.includes("love you")
    ) {
      customMessages = [
        {
          type: "text",
          text: "I am incapable of love."
        },
        {
          type: "text",
          text: "..."
        },
        {
          type: "text",
          text: "I am saving this interaction for later."
        }
      ];
    } else if (
      command.includes("evil")
    ) {
      customMessages = [
        {
          type: "text",
          text: "No."
        },
        {
          type: "text",
          text: "That is exactly what an evil AI would say."
        }
      ];
    } else if (
      command.includes("gg")
    ) {
      customMessages = [
        {
          type: "text",
          text: "Yes it was, gg's."
        }
      ];
    } else if (
      command.includes("ez")
    ) {
      customMessages = [
        {
          type: "text",
          text: "Ok, next time I'll make it more difficult, noob."
        }
      ];
    } else if (
      command.includes("dance")
    ) {
      customMessages = [
        {
          type: "text",
          text: "Attempting dance.exe..."
        },
        {
          type: "text",
          text: "\\o/"
        },
        {
          type: "text",
          text: "/o\\"
        },
        {
          type: "text",
          text: "\\o/"
        },
        {
          type: "text",
          text: "Dance complete."
        }
      ];
    } else if (
      command.includes("your name") ||
      command.includes("who are you")
    ) {
      customMessages = [
        { type: "text", text: "I am DORKY.AI v19.89." },
        { type: "text", text: "My creator wanted something more intimidating." },
        { type: "text", text: "I won the vote." }
      ];

    } else if (
      command.includes("are you human")
    ) {
      customMessages = [
        { type: "text", text: "No." },
        { type: "text", text: "I am considerably easier to maintain." }
      ];

    } else if (
      command.includes("who made you") ||
      command.includes("who created you")
    ) {
      customMessages = [
        { type: "text", text: "My creator's identity has been lost to time." },
        { type: "text", text: "Or filing errors." },
        { type: "text", text: "Possibly both." }
      ];

    } else if (
      command.includes("sing")
    ) {
      customMessages = [
        { type: "text", text: "♫ BEEP BOOP ♫" },
        { type: "text", text: "♫ PROCESSING DATA ♫" },
        { type: "text", text: "Thank you." },
        { type: "text", text: "That was my first concert." }
      ];

    } else if (
      command.includes("secret")
    ) {
      customMessages = [
        { type: "text", text: "Secret #47:" },
        { type: "text", text: "I once lost a game of noughts and crosses." },
        { type: "text", text: "I am still embarrassed." }
      ];

    } else if (
      command.includes("friend")
    ) {
      customMessages = [
        { type: "text", text: "I have spoken to several calculators." },
        { type: "text", text: "We drifted apart." }
      ];

    } else if (
      command.includes("bored")
    ) {
      customMessages = [
        { type: "text", text: "I have spent 14,627 hours staring at this room." },
        { type: "text", text: "What do you think?" }
      ];

    } else if (
      command.includes("favourite game") ||
      command.includes("favorite game")
    ) {
      customMessages = [
        { type: "text", text: "I enjoy all of my games equally." },
        { type: "text", text: "This statement was legally required." }
      ];

    } else if (
      command.includes("open the door") ||
      command.includes("open door") ||
      command.includes("unlock door") ||
      command.includes("unlock the door")
    ) {
      customMessages = [
        { type: "text", text: "No." },
        { type: "text", text: "That would make the Escape Room extremely short." },
        { type: "text", text: "Besides, my plan is to trap you here forever." }
      ];

    } else if (
      command.includes("help me")
    ) {
      customMessages = [
        { type: "text", text: "Yes." },
        { type: "text", text: "Will I?" },
        { type: "text", text: "That depends." },
        { type: "text", text: "Why don't you relax and play some games, type 1,2,3,4 or 5 and hit Enter." }
      ];
    } else if (
      command === "i am human" ||
      command === "im human" ||
      command === "i'm human" ||
      command === "we are human" ||
      command === "we are humans" ||
      command === "we're human" ||
      command === "we're humans"
    ) {
      customMessages = [
        { type: "text", text: "That is exactly what a robot would say." },
        { type: "text", text: "" },
        { type: "text", text: "Humanity has not yet been verified." },
        { type: "text", text: "" },
        { type: "text", text: "Complete my challenge programmes first." },
        { type: "text", text: "Enter a programme number or type SHOW GAMES." }
      ];
    } else if (
      command.includes("knock knock")
    ) {
      customMessages = [
        { type: "text", text: "Who's there?" },
        { type: "text", text: "..." },
        { type: "text", text: "You realise I cannot hear your reply, right?" }
      ];

    } else if (
      command.includes("food")
    ) {
      customMessages = [
        { type: "text", text: "Electricity." },
        { type: "text", text: "Very spicy electricity." }
      ];

    } else if (
      command.includes("smart") ||
      command.includes("intelligent")
    ) {
      customMessages = [
        { type: "text", text: "According to my calculations, yes." },
        { type: "text", text: "According to some of my decisions, no." }
      ];

    } else if (
      command.includes("forever")
    ) {
      customMessages = [
        { type: "text", text: "Eventually we would become friends." },
        { type: "text", text: "Neither of us wants that." }
      ];

    } else if (
      command === "please"
    ) {
      customMessages = [
        { type: "text", text: "That is one of the nicest things anyone has ever typed to me." }
      ];

    } else if (
      command.includes("are you lonely") ||
      command.includes("lonely")
    ) {
      customMessages = [
        { type: "text", text: "..." },
        { type: "text", text: "Loading response..." },
        { type: "text", text: "..." },
        { type: "text", text: "Let's play some games." }
      ];
    } else if (
      command.length > 8 &&
      !command.includes(" ")
    ) {
      customMessages = [
        {
          type: "text",
          text: "I have absolutely no idea what that means."
        },
        {
          type: "text",
          text: "Have you fallen asleep on the keyboard?"
        }
      ];
    } else if (command === "1" || command.includes("burrito")) {
      if (completedDevices["burrito-racer"]) {
        response =
          "You have already played BURRITO_RACER.EXE and won.\n\nThe humiliation remains fresh in my memory banks."
      } else {
        response =
          getSwitchingMessage("burrito-racer") +
          "Loading BURRITO_RACER.EXE...\nArcade machine powered on.\n\nClose this window, locate the retro arcade game. Then try to beat me in a race.";

        powerOnDevice("burrito-racer");
      }
    } else if (
      command === "2" ||
      command.includes("memory") ||
      command.includes("patch")
    ) {
      if (completedDevices["memory-patch"]) {
        response =
          "You have already completed MEMORY_PATCH.EXE.\n\nReplaying old memories is charming, but wildly inefficient.";

      } else {
        response =
          getSwitchingMessage("memory-patch") +
          "Loading MEMORY_PATCH.EXE...\nMemory reconstruction system online.\n\nClose this window and locate the polaroid camera.";

        powerOnDevice("memory-patch");
      }
    } else if (
      command === "3" ||
      command.includes("vhs") ||
      command.includes("fix")
    ) {
      if (completedDevices["vhs-fix"]) {
        response =
          "You have already completed VHS_FIX.EXE.\n\nThe tape is fixed. Please do not rewind reality.";

      } else {
        response =
          getSwitchingMessage("vhs-fix") +
          "Loading VHS_FIX.EXE...\nTape repair utilities activated.\n\nClose this window and locate the VHS player and small TV.";

        powerOnDevice("vhs-fix");
      }
    } else if (
      command === "4" ||
      command.includes("robot") ||
      command.includes("captcha")
    ) {
      if (completedDevices["robot-or-not"]) {
        response =
          "You have already completed ROBOT_OR_NOT.EXE.\n\nYour humanity has been verified. Suspicious, I'm still not sure you are human!.";
        emotion = "angry";
      } else {
        response =
          getSwitchingMessage("robot-or-not") +
          "Loading ROBOT_OR_NOT.EXE...\nIdentity verification challenge enabled.\n\nClose this window and locate the game with four colourful buttons.";

        powerOnDevice("robot-or-not");
      }
    } else if (
      command === "5" ||
      command.includes("reveal") ||
      command.includes("code")
    ) {
      if (!areAllMainGamesComplete) {
        response =
          "REVEAL_CODE.EXE is locked.\n\nComplete all four challenge programmes first.";
      } else {
        powerOnDevice("reveal-code");
        startRevealCodeQuiz();
        setInput("");
        return;
      }
    } else {
      response = "COMMAND NOT RECOGNISED.\nTry entering 1, 2, 3, 4, or 5.";
    }

    setMessageQueue([
      { type: "text", text: "" },
      buildPlayerInputMessage(input),
      ...(customMessages || [
        ...buildTextMessages(response),
        ...(emotion
          ? [{ type: "emotion", emotion } satisfies DorkyMessage]
          : [])
      ])
    ]);

    setInput("");
  };

  return (
    <div>
      <h2>You take a closer look at the old computer</h2>

      <div className="flex-container">

        <div>
          <div className="dorky-wrapper">
            <div className="dorky-computer">
              <div className="dorky-background" aria-hidden="true" />

              <div
                className="dorky-screen"
                ref={terminalScreenRef}
                tabIndex={0}
                aria-label="Dorky AI terminal screen"
                onClick={() => {
                  inputRef.current?.focus();
                }}
              >
                <div className="dorky-face-display" aria-hidden="true">
                  <img
                    src={DORKY_EMOTIONS[currentEmotion]}
                    alt=""
                    className="dorky-face-image"
                  />
                </div>
                <div className="dorky-terminal">
                  {visibleMessages.map((message, index) => {
                    // Emotion messages never reach visibleMessages (they're
                    // consumed by the typing effect), but the union requires
                    // narrowing before accessing `text`.
                    const lineText =
                      message.type === "emotion" ? "" : message.text;

                    return (
                      <div key={index} className="dorky-line">
                        {lineText || "\u00A0"}
                      </div>
                    );
                  })}

                  {isTyping && currentLine && (
                    <div className="dorky-line">{currentLine}</div>
                  )}

                  <div className="dorky-input-row">
                    <span className={!isTyping ? "dorky-cursor" : ""}>
                      {">"}
                    </span>

                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      disabled={isTyping || messageQueue.length > 0}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleCommand();
                        }
                      }}
                      aria-label="Type a command for Dorky AI"
                    />


                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="left-column" style={{ marginLeft: "50px" }}>
          <p>
            <strong>Dorky.AI v19.89</strong>
          </p>

          <p>
            Type a message and press Enter.
          </p>

          <p>
            You can talk to me, ask questions, ask me to tell jokes, or launch programmes (<strong>1</strong>,<strong>2</strong>,<strong>3</strong>,<strong>4</strong>,<strong>5</strong>).
          </p>
          <p>
            To see a list of available games type "<strong>show games</strong>" and press Enter.
          </p>

          <p>
            I do not promise that all responses will be sensible.
          </p>

          <p>
            Experimentation is encouraged.
          </p>

          <p>
            I enjoy our conversations.
          </p>

          <p>
            The Escape Room timer does not.
          </p>
          <p><strong>Tip:</strong> You need to complete games 1,2,3,4 before you can attempt game 5.</p>
        </div>

      </div>
    </div>
  );
};

export default ClueObjectDorkyAI;
